/**
 * Bedienelemente, die das laufende Programm angemeldet hat (Rueckkanal).
 *
 * Das Programm bestimmt, was es gibt: jeder Aufruf von `remote.number`,
 * `remote.switch` oder `remote.button` meldet ein Element an. Die Oberflaeche
 * kennt die Bedeutung nicht - sie schickt nur den Namen und den Wert zurueck.
 *
 * Damit wird aus "Programm aendern, uebertragen, messen, ansehen" ein Schieben
 * und Zusehen; der Zusammenhang zwischen Wert und Kurve ist unmittelbar
 * sichtbar.
 */

import { Button, Text } from "@microbit/ui";
import { useCallback, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { HStack, VStack } from "styled-system/jsx";
import { formatValue } from "./chart";
import type { RemoteControl, RemoteValue } from "./parse";
import { useTelemetry } from "./telemetry-hooks";

interface EinzelnProps {
  control: RemoteControl;
  value: RemoteValue | undefined;
  onChange: (value: RemoteValue) => void;
}

/** Kantenlaenge des Joystickfeldes in Pixeln. */
const JOYSTICK_GROESSE = 120;

/** Schrittweite je Pfeiltaste, als Anteil des Vollausschlags. */
const TASTENSCHRITT = 0.25;

/**
 * Auf zwei Stellen runden.
 *
 * Nicht Kosmetik: jede Aenderung schickt eine Nachricht ueber dieselbe Leitung
 * wie die Konsole. Ohne Rundung entsteht bei jeder Mausbewegung eine, und der
 * Schieberegler daneben macht es aus demselben Grund in hundert Stufen.
 */
const runden = (wert: number) => {
  const gerundet = Math.round(wert * 100) / 100;
  // Negative Null glaetten: -0 entsteht beim Rechnen mitten im Feld, erscheint
  // in der Anzeige als "-0.00" und geht so auch ueber die Leitung. Numerisch
  // harmlos, in einer Lernoberflaeche aber eine unnoetige Frage.
  return gerundet === 0 ? 0 : gerundet;
};

const begrenzen = (wert: number) => Math.max(-1, Math.min(1, wert));

interface JoystickProps {
  name: string;
  wert: [number, number];
  onChange: (wert: [number, number]) => void;
}

/**
 * Ein federnder Joystick: losgelassen geht er in die Ruhelage zurueck.
 *
 * **Die y-Achse wird gedreht.** Auf dem Bildschirm zeigt y nach unten, der
 * Roboter faehrt bei positivem y vorwaerts. Die Drehung passiert hier, damit im
 * Schuelerprogramm die Richtung steht, die der Roboter faehrt - und nicht die,
 * die der Bildschirm meint.
 *
 * Mit Tastatur bedienbar: Pfeiltasten schlagen aus, Loslassen zentriert. Ohne
 * das waere das Bedienelement fuer jeden unbenutzbar, der keine Maus fuehren
 * kann.
 */
const Joystick = ({ name, wert, onChange }: JoystickProps) => {
  const feld = useRef<HTMLDivElement>(null);
  const [zieht, setZieht] = useState(false);
  const [x, y] = wert;

  const ausPunkt = useCallback(
    (clientX: number, clientY: number) => {
      const kasten = feld.current?.getBoundingClientRect();
      if (!kasten) {
        return;
      }
      const halb = kasten.width / 2;
      const neuX = begrenzen((clientX - kasten.left - halb) / halb);
      // Minus: Bildschirm nach unten, Roboter nach vorn.
      const neuY = begrenzen(-(clientY - kasten.top - halb) / halb);
      onChange([runden(neuX), runden(neuY)]);
    },
    [onChange]
  );

  const zentrieren = useCallback(() => {
    setZieht(false);
    onChange([0, 0]);
  }, [onChange]);

  return (
    <VStack alignItems="stretch" gap="0" width="100%">
      <HStack justifyContent="space-between" alignItems="baseline">
        <Text fontSize="xs">{name}</Text>
        <Text fontSize="xs" fontFamily="code" color="fg.muted">
          {formatValue(x)} / {formatValue(y)}
        </Text>
      </HStack>
      <div
        ref={feld}
        role="application"
        aria-label={name}
        tabIndex={0}
        data-testid={`remote-${name}`}
        onPointerDown={(event) => {
          // Der Zeiger wird eingefangen, damit das Ziehen weitergeht, wenn die
          // Maus das Feld verlaesst. Optional aufgerufen: es ist eine Zugabe,
          // und ein Bedienelement soll nicht daran scheitern, dass eine
          // Umgebung sie nicht kennt.
          event.currentTarget.setPointerCapture?.(event.pointerId);
          setZieht(true);
          ausPunkt(event.clientX, event.clientY);
        }}
        onPointerMove={(event) => {
          if (zieht) {
            ausPunkt(event.clientX, event.clientY);
          }
        }}
        onPointerUp={zentrieren}
        onPointerCancel={zentrieren}
        onKeyDown={(event) => {
          const schritt: Record<string, [number, number]> = {
            ArrowLeft: [-TASTENSCHRITT, 0],
            ArrowRight: [TASTENSCHRITT, 0],
            ArrowUp: [0, TASTENSCHRITT],
            ArrowDown: [0, -TASTENSCHRITT],
          };
          const richtung = schritt[event.key];
          if (richtung) {
            event.preventDefault();
            onChange([
              runden(begrenzen(x + richtung[0])),
              runden(begrenzen(y + richtung[1])),
            ]);
          } else if (event.key === "Escape" || event.key === " ") {
            event.preventDefault();
            onChange([0, 0]);
          }
        }}
        onKeyUp={(event) => {
          if (event.key.startsWith("Arrow")) {
            onChange([0, 0]);
          }
        }}
        onBlur={zentrieren}
        style={{
          position: "relative",
          width: `${JOYSTICK_GROESSE}px`,
          height: `${JOYSTICK_GROESSE}px`,
          margin: "0 auto",
          borderRadius: "50%",
          border: "1px solid currentColor",
          opacity: 0.85,
          touchAction: "none",
          cursor: zieht ? "grabbing" : "grab",
        }}
      >
        {/* Kreuz durch die Ruhelage, damit sie ohne Zahl erkennbar ist. */}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "50%",
            borderTop: "1px solid currentColor",
            opacity: 0.25,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "50%",
            borderLeft: "1px solid currentColor",
            opacity: 0.25,
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            width: "22px",
            height: "22px",
            borderRadius: "50%",
            background: "currentColor",
            left: `calc(50% + ${(x * JOYSTICK_GROESSE) / 2}px - 11px)`,
            top: `calc(50% - ${(y * JOYSTICK_GROESSE) / 2}px - 11px)`,
          }}
        />
      </div>
    </VStack>
  );
};

const Einzeln = ({ control, value, onChange }: EinzelnProps) => {
  if (control.kind === "joystick") {
    const paar: [number, number] = Array.isArray(value) ? value : [0, 0];
    return <Joystick name={control.name} wert={paar} onChange={onChange} />;
  }

  if (control.kind === "button") {
    return (
      <Button
        size="xs"
        variant="secondary"
        onPress={() => onChange(true)}
        data-testid={`remote-${control.name}`}
      >
        {control.name}
      </Button>
    );
  }

  if (control.kind === "switch") {
    const an = value === true;
    return (
      <HStack justifyContent="space-between" alignItems="center" width="100%">
        <Text fontSize="xs">{control.name}</Text>
        <Button
          size="xs"
          variant={an ? "primary" : "secondary"}
          onPress={() => onChange(!an)}
          data-testid={`remote-${control.name}`}
        >
          <FormattedMessage id={an ? "remote-on" : "remote-off"} />
        </Button>
      </HStack>
    );
  }

  const zahl = typeof value === "number" ? value : control.min ?? 0;
  const min = control.min ?? 0;
  const max = control.max ?? 1;
  // Hundert Stufen ueber den Bereich: fein genug zum Einstellen, grob genug,
  // dass nicht bei jeder Mausbewegung eine Nachricht entsteht.
  const step = (max - min) / 100;
  return (
    <VStack alignItems="stretch" gap="0" width="100%">
      <HStack justifyContent="space-between" alignItems="baseline">
        <Text fontSize="xs">{control.name}</Text>
        <Text fontSize="xs" fontFamily="code" color="fg.muted">
          {formatValue(zahl)}
        </Text>
      </HStack>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={zahl}
        aria-label={control.name}
        data-testid={`remote-${control.name}`}
        onChange={(event) => onChange(Number(event.target.value))}
        style={{ width: "100%" }}
      />
    </VStack>
  );
};

export const NurFuerPruefung = { Joystick, Einzeln };

const RemoteControls = () => {
  const { controls, controlValues, setControlValue } = useTelemetry();
  if (controls.length === 0) {
    return null;
  }
  return (
    <VStack
      alignItems="stretch"
      gap="2"
      pt="2"
      pb="2"
      borderTopWidth="1px"
      borderColor="gray.200"
    >
      <Text fontSize="xs" fontWeight="semibold">
        <FormattedMessage id="remote-title" />
      </Text>
      {controls.map((control) => (
        <Einzeln
          key={control.name}
          control={control}
          value={controlValues[control.name]}
          onChange={(value) => setControlValue(control.name, value)}
        />
      ))}
    </VStack>
  );
};

export default RemoteControls;
